import type { CaptureResult } from "posthog-js";
import {
  CHUNK_RELOAD_SESSION_KEY,
  DOM_MUTATION_RELOAD_SESSION_KEY,
  INDEXEDDB_RELOAD_SESSION_KEY,
  isChunkLoadFailureMessage,
  isChunkMimeTypeMismatchMessage,
  isDomMutationMessage,
  isIndexedDbDisconnectMessage,
  isSafariEmptyRangesMessage,
} from "./recoveryErrors";
import { isThirdPartyInjectedNoise, type NoiseFrame } from "./thirdPartyNoise";

interface ExceptionListEntry {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: Array<{ filename?: string | null; function?: string | null }>;
  };
}

function getSessionStorageFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/**
 * PostHog `before_send` filter for `$exception` events. Mirrors the drop and
 * tagging decisions of the Sentry `beforeSend` in main.tsx, but message-based
 * only — PostHog exception events carry no symbolication metadata, so the
 * frame-quality checks Sentry applies (Safari EmptyRanges) are not replicated.
 * Non-exception events pass through untouched.
 */
export function dropNoisyPostHogExceptions(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event || event.event !== "$exception") {
    return event;
  }

  const exceptionList =
    (event.properties?.$exception_list as ExceptionListEntry[] | undefined) ?? [];
  const messages = exceptionList.map(
    (entry) => `${entry.type ?? ""}: ${entry.value ?? ""}`
  );
  const frames: NoiseFrame[] = exceptionList.flatMap(
    (entry) => entry.stacktrace?.frames ?? []
  );

  // Same hard drops as Sentry's ignoreErrors/beforeSend: injected third-party
  // scripts and device-network noise, nothing of ours to fix.
  const isHardDropNoise = messages.some(
    (message) =>
      (/change_ua/i.test(message) && /already been declared/i.test(message)) ||
      /auth\/network-request-failed/i.test(message)
  );
  if (isHardDropNoise || isThirdPartyInjectedNoise({ messages, frames })) {
    return null;
  }

  // Keep the recoverable browser families but stamp the same tags Sentry gets,
  // so Error Tracking can separate auto-recovered noise from real breakage.
  const hasIndexedDbDisconnect = messages.some(isIndexedDbDisconnectMessage);
  const hasSafariEmptyRanges = messages.some(isSafariEmptyRangesMessage);
  const hasChunkLoadFailure = messages.some(isChunkLoadFailureMessage);
  const hasDomMutation = messages.some(isDomMutationMessage);

  let recoveryErrorFamily: string | undefined;
  let reloadSessionKey: string | undefined;
  if (hasIndexedDbDisconnect) {
    recoveryErrorFamily = "indexeddb_disconnect";
    reloadSessionKey = INDEXEDDB_RELOAD_SESSION_KEY;
  } else if (hasSafariEmptyRanges) {
    recoveryErrorFamily = "safari_emptyranges_reference";
  } else if (hasChunkLoadFailure) {
    recoveryErrorFamily = messages.some(isChunkMimeTypeMismatchMessage)
      ? "chunk_mime_type_mismatch"
      : "chunk_load_failure";
    reloadSessionKey = CHUNK_RELOAD_SESSION_KEY;
  } else if (hasDomMutation) {
    recoveryErrorFamily = "dom_mutation";
    reloadSessionKey = DOM_MUTATION_RELOAD_SESSION_KEY;
  }

  if (recoveryErrorFamily) {
    event.properties = {
      ...event.properties,
      recovery_error_family: recoveryErrorFamily,
      auto_reload_attempted: reloadSessionKey
        ? getSessionStorageFlag(reloadSessionKey)
        : false,
    };
  }

  return event;
}
